import { OutlineIcon } from "#web/assets/OutlineIcon";
import { CardSkeleton } from "#web/components/common/CardSkeleton";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { VerifyCheckoutExtensionInstalledQuery } from "#web/components/shop/queries/verifyCheckoutExtensionInstalledQuery";
import { VerifyThemeAppExtensionIsActiveQuery } from "#web/components/shop/queries/verifyThemeAppExtensionActiveQuery";
import { routeConfig } from "#web/configs/route";
import { i18nLike } from "#web/services/i18nLike";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  Collapsible,
  Icon,
  Image,
  InlineStack,
  Modal,
  ProgressBar,
  Spinner,
  Text,
} from "@shopify/polaris";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as s from "./OnboardingGuide.css";

const en = {
  addCheckoutExtension: {
    description:
      "Add the Checkout Extension to your store to start blocking codes on your checkout page. (Once added it might take a few minutes to take effect)",
    primaryButton: "Add Checkout Extension",
    title: "Add Checkout Extension",
  },
  addThemeExtension: {
    description: "Add the Theme Extension to your store to start blocking extension pop-ups.",
    primaryButton: "Add Theme Extension",
    title: "Add Theme Extension",
  },
  checkIconAccessibilityLabel: "Check icon to indicate completion of Setup Guide",
  completed: "Setup complete",
  description: "Complete these steps to protect your store. This does not impact your store's speed.",
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "There was an error loading the onboarding guide",
  },
  openCheckoutSettings: "Open Checkout Settings",
  seeDocumentation: "See documentation",
  setAlertEmail: {
    description: "Set an alert email address to receive notifications when leaked codes are detected.",
    primaryButton: "Set Alert Email",
    title: "Set an Email for Alerts",
  },
  title: "Get started with KeepCart",
};

export const OnboardingGuide = () => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const {
    data: checkoutExtensionData,
    isError: checkoutExtensionError,
    isFetching: checkoutExtensionFetching,
  } = useQuery(VerifyCheckoutExtensionInstalledQuery({ shopId: shop?.id }));
  const {
    data: themeExtensionData,
    isError: themeExtensionError,
    isFetching: themeExtensionFetching,
  } = useQuery(VerifyThemeAppExtensionIsActiveQuery({ shopId: shop?.id }));

  const [checkoutModalOpen, setCheckoutModalOpen] = useState<boolean>(false);
  const [themeModalOpen, setThemeModalOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  const items = [
    {
      complete: !!checkoutExtensionData?.installed,
      description: t("addCheckoutExtension.description"),
      id: 0,
      loading: checkoutExtensionFetching,
      primaryButton: {
        content: t("addCheckoutExtension.primaryButton"),
        props: {
          onClick: () => {
            setThemeModalOpen(false);
            setCheckoutModalOpen(true);
          },
        },
      },
      show: shop!.subscribedTo("blocking"),
      title: t("addCheckoutExtension.title"),
    },
    {
      complete: !!themeExtensionData?.isActive,
      description: t("addThemeExtension.description"),
      id: 1,
      loading: themeExtensionFetching,
      primaryButton: {
        content: t("addThemeExtension.primaryButton"),
        props: {
          onClick: () => {
            setCheckoutModalOpen(false);
            setThemeModalOpen(true);
          },
        },
      },
      show: true,
      title: t("addThemeExtension.title"),
    },
    {
      complete: Array.isArray(shop?.alertEmails) && shop.alertEmails.length > 0,
      description: t("setAlertEmail.description"),
      id: 2,
      loading: shopFetching,
      primaryButton: {
        content: t("setAlertEmail.primaryButton"),
        props: {
          onClick: () => {
            navigate(routeConfig.settings["/"], { state: { tab: "alerts" } });
          },
        },
      },
      show: !shop?.subscribedTo("freemium"),
      title: t("setAlertEmail.title"),
    },
  ];

  const nextIncompleteIndex = items.findIndex((item) => !item.complete);
  const [expanded, setExpanded] = useState<number>(nextIncompleteIndex > -1 ? nextIncompleteIndex : 0);

  const accessId = useId();
  const completedItemsLength = items.filter((item) => item.complete && item.show).length;
  const shownItemsCount = items.filter((item) => item.show).length;
  const [isGuideOpen, setIsGuideOpen] = useState<boolean>(completedItemsLength === shownItemsCount);

  const isError = shopError || checkoutExtensionError || themeExtensionError;
  const isLoading = shopFetching || checkoutExtensionFetching || themeExtensionFetching;

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  if (isLoading) {
    return <CardSkeleton />;
  }

  return (
    <>
      <Card padding="500">
        <BlockStack gap="100">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingMd">
              {t("title")}
            </Text>
            <ButtonGroup gap="tight" noWrap>
              <Button
                ariaControls={accessId}
                icon={isGuideOpen ? ChevronUpIcon : ChevronDownIcon}
                onClick={() => {
                  setIsGuideOpen((prev) => {
                    if (!prev) {
                      setExpanded(items.findIndex((item) => !item.complete));
                    }
                    return !prev;
                  });
                }}
                variant="tertiary"
              />
            </ButtonGroup>
          </InlineStack>
          <Text as="p" variant="bodyMd">
            {t("description")}
          </Text>
          <div style={{ marginTop: ".8rem" }}>
            <InlineStack blockAlign="center" gap="200">
              {completedItemsLength === shownItemsCount ? (
                <div style={{ maxHeight: "1rem" }}>
                  <InlineStack gap="100" wrap={false}>
                    <Icon accessibilityLabel={t("checkIconAccessibilityLabel")} source={CheckIcon} tone="subdued" />
                    <Text as="p" tone="subdued" variant="bodySm">
                      {t("completed")}
                    </Text>
                  </InlineStack>
                </div>
              ) : (
                <Text as="span" variant="bodySm">
                  {`${String(completedItemsLength)} / ${String(shownItemsCount)} ${t("completed")}`}
                </Text>
              )}

              {completedItemsLength !== shownItemsCount ? (
                <div style={{ width: "100px" }}>
                  <ProgressBar
                    animated
                    progress={(completedItemsLength / shownItemsCount) * 100}
                    size="small"
                    tone="primary"
                  />
                </div>
              ) : null}
            </InlineStack>
          </div>
        </BlockStack>
        <Collapsible id={accessId} open={isGuideOpen}>
          <Box paddingBlockStart="400">
            <BlockStack gap="100">
              {items
                .filter((item) => item.show)
                .map((item) => (
                  <SetupItem
                    expanded={expanded === item.id}
                    key={item.id}
                    setExpanded={() => {
                      setExpanded(item.id);
                    }}
                    {...item}
                  />
                ))}
            </BlockStack>
          </Box>
        </Collapsible>
      </Card>

      {/* Add Checkout Extension Modal */}
      <Modal
        loading={shopFetching}
        onClose={() => {
          setCheckoutModalOpen(false);
        }}
        open={checkoutModalOpen}
        primaryAction={{
          content: t("openCheckoutSettings"),
          onAction: shop?.myshopifyDomain
            ? () => window.open(`https://${shop.myshopifyDomain!}/admin/settings/checkout`, "_blank")
            : () => void 0,
        }}
        secondaryActions={[
          {
            content: t("seeDocumentation"),
            onAction: () => window.open("https://www.keepcart.co/extension-blocking-newcheckout", "_blank"),
          },
        ]}
        title={t("addCheckoutExtension")}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              You first go to <b>Settings &gt; Checkout</b> and click <b>Customize</b>.
            </Text>
            <Image
              alt="Checkout Extension"
              source="https://cdn.prod.website-files.com/64467f5d1f692e0ac55bcdbd/6797dcfe3577ff677034a34f_DOCS-1.png"
            />
          </BlockStack>
        </Modal.Section>
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              Once you're here, click on <b>Apps</b> on the left side and then go to <b>KeepCart</b> and click{" "}
              <b>Add to Checkout</b> .
            </Text>
            <Image
              alt="Checkout Extension"
              source="https://cdn.prod.website-files.com/64467f5d1f692e0ac55bcdbd/6705809a26b65e2b69134eaa_DOCS-2-p-1600.png"
            />
          </BlockStack>
        </Modal.Section>
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              Then toggle on <b>Include app block in Shop Pay</b>
            </Text>
            <Image
              alt="Checkout Extension"
              source="https://cdn.prod.website-files.com/64467f5d1f692e0ac55bcdbd/67057187c8d45dfcf1c258fd_DOCS-3.png"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Add Theme Extension Modal */}
      <Modal
        loading={shopFetching}
        onClose={() => {
          setThemeModalOpen(false);
        }}
        open={themeModalOpen}
        primaryAction={{
          content: "Open Theme Settings",
          onAction: () => {
            const template = "index";
            const uuid = "6c9b264a-3284-48f2-a6f6-2836be6ada2a";
            const handle = "keepcart";
            const deepLink = `https://${shop?.myshopifyDomain ?? ""}/admin/themes/current/editor?context=apps&template=${template}&activateAppId=${uuid}/${handle}`;
            window.open(deepLink, "_blank");
          },
        }}
        secondaryActions={[
          {
            content: "See documentation",
            onAction: () => window.open("https://www.keepcart.co/extension-blocking-newcheckout", "_blank"),
          },
        ]}
        title="Add Theme Extension"
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              Turn the KeepCart App Embed by going to <b>Online Store / Theme / Customize / App Embeds</b>
            </Text>
            <Image
              alt="Theme Extension"
              source="https://cdn.prod.website-files.com/64467f5d1f692e0ac55bcdbd/6705766ede481970d63aafec_DOCS-4.png"
            />
          </BlockStack>
        </Modal.Section>
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              Once you're here, toggle the KeepCart app embed on.
            </Text>
            <Image
              alt="Theme Extension"
              source="https://cdn.prod.website-files.com/64467f5d1f692e0ac55bcdbd/66ac02296f8da7b898c6a409_kc-step3.jpeg"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
};

interface SetupItemProps {
  complete: boolean;
  description: string;
  expanded: boolean;
  id: number;
  loading?: boolean;
  primaryButton?: {
    content: string;
    props: {
      onClick: () => void;
    };
  };
  secondaryButton?: {
    content: string;
    props: {
      onClick: () => void;
    };
  };
  setExpanded: () => void;
  show: boolean;
  title: string;
}

const SetupItem = ({
  complete,
  description,
  expanded,
  id,
  loading = false,
  primaryButton,
  secondaryButton,
  setExpanded,
  title,
}: SetupItemProps) => {
  return (
    <Box
      background={expanded ? "bg-surface-active" : undefined}
      borderRadius="200"
      paddingBlock="100"
      paddingInline="300"
    >
      <div className={`${s.SetupItem} ${expanded ? s.isExpanded : ""}`}>
        <InlineStack gap="200" wrap={false}>
          <div>
            {loading ? (
              <Spinner size="small" />
            ) : complete ? (
              <div className={s.CheckIconContainer}>
                <CheckIcon className={s.CheckIcon} />
              </div>
            ) : (
              <OutlineIcon />
            )}
          </div>
          <div
            className={s.ItemContent}
            onClick={expanded ? () => null : setExpanded}
            style={{
              cursor: expanded ? "default" : "pointer",
              paddingTop: ".15rem",
              width: "100%",
            }}
          >
            <BlockStack gap="100" id={String(id)}>
              <Text as="h4" variant={expanded ? "headingSm" : "bodyMd"}>
                {title}
              </Text>
              <Collapsible id={String(id)} open={expanded}>
                <Box paddingBlockEnd="300">
                  <BlockStack gap="400">
                    <Text as="p" variant="bodyMd">
                      {description}
                    </Text>
                    {(primaryButton ?? secondaryButton) && (
                      <ButtonGroup gap="loose">
                        {primaryButton && (
                          <Button variant="primary" {...primaryButton.props}>
                            {primaryButton.content}
                          </Button>
                        )}
                        {secondaryButton && (
                          <Button variant="tertiary" {...secondaryButton.props}>
                            {secondaryButton.content}
                          </Button>
                        )}
                      </ButtonGroup>
                    )}
                  </BlockStack>
                </Box>
              </Collapsible>
            </BlockStack>
          </div>
        </InlineStack>
      </div>
    </Box>
  );
};
